/**
 * Program listing — model types & constants
 */
import type { RefObject } from "react";
import type { ActiveFilters, FilterSection } from "@/components/atoms/ProgramFilters/ProgramFilters";
import type { ProgramCardProps } from "@/components/atoms/ProgramCard/ProgramCard";

export type { ActiveFilters, FilterSection };

export interface Program {
  id?: string;
  programName?: string;
  programType?: string;
  duration?: string;
  domain?: string | string[];
  specializations?: string[];
  fee?: string;
  image?: string;
  partnerName?: string;
  isQSRanked?: boolean;
  description?: string;
  href?: string;
  trendingType?: string | null;
  sponseredKey?: string | null;
  sponsoredBy?: string | null;
  sortBy?: string[];
  attributes?: {
    slug?: string;
    BasicDetails?: {
      Heading?: string;
      sponsoredLogoURL: string;
      ProgramType?: string;
      enabled?: boolean;
      url_key?: string;
      CourseStripText?: string;
      SponsoredKey?: string | null;
      TrendingType?: string | null;
      DisplayPriority?: number | null;
      PROGRAM_ID?: string;
      Tags?: string[] | null;
      CardShortDescription?: string | null;
      Image?: {
        source?: {
          data?: {
            id?: string;
            attributes?: {
              url?: string;
              alternativeText?: string | null;
            };
          }[];
        };
      };
      ShortSponsoredImage?: {
        data?: {
          attributes?: {
            url?: string;
          };
        };
      };
    };
    BannerSlider?: {
      Duration?: { text?: string };
      Heading?: { text?: string };
    };
    program_domain?: {
      data?: { attributes?: { DomainHeading?: string } };
    };
    Specialization?: {
      specializations?: {
        data?: { attributes?: { BasicDetails?: { Heading?: string } } }[];
      };
    };
    Fees?: {
      FeesOptions?: {
        FeesInInr?: string | null;
        FeesInUSD?: string | null;
        Type?: string | null;
      }[];
    };
  };
}

export interface ProgramListingSectionProps {
  programs: Program[];
  country?: string;
  certifications?: Program[];
  hiddenProgramTypes?: string[];
  lockedProgramType?: string;
}

export const INDUSTRY_PARTNER_KEY_MAP: Record<string, string | null> = {
  "School of AI": null,
  Paytm: "paytm",
  Medvarsity: "medvarsity",
  HCLtech: "hcltech",
  "TCS Ion": "tcs",
  KPMG: "kpmg",
  RICS: "rics",
  PMI: "pmi",
};

export const PROGRAM_TYPE_OPTIONS = [
  "UG Programs",
  "PG Programs",
  "Certifications",
];
export const BASE_DOMAIN_OPTIONS = [
  "Management",
  "Information Technology",
  "Arts & Humanities",
  "Commerce",
];
export const INDUSTRY_PARTNER_OPTIONS = [
  "School of AI",
  "Paytm",
  "Medvarsity",
  "HCLtech",
  "TCS Ion",
  "KPMG",
  "RICS",
  "PMI",
];

export const SORT_OPTIONS = [
  "Most Popular",
  "Newest",
  "Fee: Low to High",
  "Fee: High to Low",
];

export const INITIAL_COUNT = 6;
export const PAGE_SIZE = 6;
export const CERT_PARTNERS = ["KPMG", "School of AI"] as const;

export const SECTION_CATEGORY_MAP: Record<string, string> = {
  programType: "Program Type",
  domain: "Domains",
  duration: "Duration",
  industryPartner: "Industry Partners",
};

export interface ProgramListingSectionViewModel {
  sectionRef: RefObject<HTMLElement | null>;
  sentinelRef: RefObject<HTMLDivElement | null>;
  filterSections: FilterSection[];
  activeFilters: ActiveFilters;
  handleFiltersChange: (filters: ActiveFilters) => void;
  certPartnerFilters: string[];
  toggleCertPartner: (partner: string) => void;
  lockedProgramType?: string;
  filteredProgramsCount: number;
  filteredCertificationsCount: number;
  sortBy: string;
  sortOpen: boolean;
  setSortOpen: (open: boolean) => void;
  setSortBy: (sort: string) => void;
  sortOptions: string[];
  cardProps: ProgramCardProps[];
  hasResults: boolean;
  clearFilters: () => void;
  hasMore: boolean;
  activeFilterCount: number;
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (open: boolean) => void;
  certPartners: readonly string[];
}
